import React, { useEffect, useState, useContext } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import { ITableConfig } from 'src/components/common/table';
import useFetch from 'use-http';
import _ from 'lodash';
import ProspectDetails from 'src/models/projectSettings/TsdiProspect';
import TsdiClient from 'src/models/AP/Client';

interface IFindProjectDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onCancel?: () => void;
    onAddNewProject?: (payload: ProspectDetails) => void;
    resetProjectSearchOptions?: () => void;
    setProjectSearchTerm?: (keyword: string) => void;
    incrementProjects?: () => void;
    projects?: ProspectDetails[];
    setSelectedProject?: (project: ProspectDetails) => void;
    setSelectedClient?: (project: TsdiClient) => void;
    style?: React.CSSProperties;
    addNewProject?: boolean;
    defaultProject?: number;
    mileageRate?: number;
    dayRate?: number;
    clientNumber?: number;
    clientTypes?: { code: number, description: string}[];
}

const FindProjectDialog = (props: IFindProjectDialogProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, errors, getValues } = useForm();
    const [fetchNextPage, setFetchNextPage] = useState<boolean>(true);
    const [projectSearchOptions, setProjectSearchOptions] = useState<ODataOptions>(undefined);
    const [projectSearchTerm, setProjectSearchTerm] = useState<string>('');
    const [projects, setProjects] = useState<Array<ProspectDetails>>(undefined);
    const [selectedProject, setSelectedProject] = useState<number>(null);
    const { get, response } = useFetch();
    // const dispatch = useDispatch();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (!!projectSearchOptions) {
            getProjects();
        }
    }, [projectSearchOptions]);

    useEffect(() => {
        if ((!!projectSearchTerm || projectSearchTerm === '') && !!projectSearchOptions) {
            resetProjectSearchOptions();
        }
    }, [projectSearchTerm]);

    useEffect(() => {
        if (!!props.clientNumber) {
            getProjects();
        }
    }, [props.clientNumber]);

    const onCreate = () => {        
        initializeClientSearchSettings();
    };

    const resetState = () => {
        setSelectedProject(props?.defaultProject);
        setProjectSearchTerm('');
        resetProjectSearchOptions();
        setProjects([]);
    };

    const OKClicked = () => {
        props.setSelectedProject(projects[selectedProject]);
        props.hideDialog();
        resetState();
    };

    const onProjectSearchChanged = (searchContent: string) => {
        search();
    };

    const onCancel = () => {
        props.hideDialog();        
        resetState();
    };

    const initializeClientSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 100,
            idField: 'PR_ID',
            pageNumber: 1,
            columns: []
        };
        setProjectSearchOptions(ODataOptions.from(tableConfig).addOrderBy('PR_NO', ODataOrderByDirection.Ascending));
    };

    const getScrollStatus = (divElement: HTMLDivElement) => {
        if (divElement) {
            const scrollTop = divElement.scrollTop % 1 > .9 ? divElement.scrollTop : Math.round(divElement.scrollTop);
            const scrollHeight = divElement.scrollHeight;
            const clientHeight = divElement.clientHeight;

            // Check if reached the end of the list
            if (scrollTop + clientHeight >= scrollHeight) {
                incrementClients();
                console.log('end of scroll YES');
            } else {
                console.log('end of scroll NO');
            }
        }
    };

    const incrementClients = () => {
        if (!!fetchNextPage) {
            let newSearchOptions = _.cloneDeep(projectSearchOptions);
            newSearchOptions.incrementPageNumber();
            setProjectSearchOptions(newSearchOptions);
        }
    };

    const selectProjectAndClose = (project: ProspectDetails) => {
        props.setSelectedProject(project);
        props.hideDialog();
        resetState();
    };

    const resetProjectSearchOptions = () => {
        setFetchNextPage(true);
        let newProjectSearchOptions = _.cloneDeep(projectSearchOptions);
        newProjectSearchOptions.clearFilters();
        newProjectSearchOptions.addPaging(1, 50);

        if (!!props?.clientNumber && !isNaN(Number(props.clientNumber))) {
            newProjectSearchOptions.addFilter(new ODataFilter('CL_NO', ODataFilterOperators.Equals, props?.clientNumber));
        }
        if (!!projectSearchTerm && !isNaN(Number(projectSearchTerm))) {
            newProjectSearchOptions.addFilter(new ODataFilter('PR_NO', ODataFilterOperators.Contains, projectSearchTerm));
        } else if (projectSearchTerm && isNaN(Number(projectSearchTerm))) {
            newProjectSearchOptions.addFilter(new ODataFilter('PR_NAME', ODataFilterOperators.Contains, projectSearchTerm));
        }
        setProjectSearchOptions(newProjectSearchOptions);
    };

    const getProjects = async () => {
        const queryString = projectSearchOptions?.toString();
        let result = await get(`/v1.0/ClientProjectMaintenance/projects/byclient/${props?.clientNumber}/${queryString}`);

        if (response.ok) {
            setProjects(result);
        } else {
            setProjects([]);
            console.log('Unable to get client data!');
        }
    };

    const search = _.debounce(() => {
        let searchString =  (!!getValues()?.searchProjectName ? getValues().searchProjectName : ' ');
        setProjectSearchTerm(searchString);
    }, 700);

    const onCreateProject = () => {
        const payload = getValues();
        const transformedPayload = new ProspectDetails();
        transformedPayload.clientNumber = !!props?.clientNumber ? props?.clientNumber : 0;
        transformedPayload.name = payload.searchProjectName;
        transformedPayload.backupOfficeRate = !isNaN(Number(payload.backupOfficeRate)) ? Number(payload.backupOfficeRate) : 250.00;
        transformedPayload.mileageRate = !isNaN(Number(payload.mileageRate)) ? Number(payload.mileageRate) : .7;
        transformedPayload.Type = 'L';
        transformedPayload.dayRate = props?.dayRate;
        transformedPayload.billMode = 0;
        transformedPayload.billBy = payload.billingbyMethod;
        transformedPayload.bopRate = payload.bopRate = 0;
        transformedPayload.turnkey = payload.turnkeyBilling;
        transformedPayload.county = payload.searchProjectCountry;
        transformedPayload.state = payload.searchProjectState;
        transformedPayload.modByNo = Number(appContext.currentUser.userId);
        transformedPayload.modByName = appContext.currentUser.username;
        props.onAddNewProject(transformedPayload);
    };

    return (
        <>
        <Dialog title={!!props.addNewProject ? 'Add Project' : 'Find Project'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ maxHeight: '75vh', width: '50%' }}>
        <form>
            <div style={{display: 'inline-block', marginTop: '1rem'}}>
                <div>
                {!!props.addNewProject && <RHFSelect 
                    label="Billing Method"
                    name={`billingFrequency`}
                    options={[
                        { value: '0', label: 'Land' }
                    ]}
                    register={register} 
                    errors={errors}
                    style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '50%'}}
                    bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '55%'}}
                />}
                <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Client #"
                    id={'searchClientNumber'}
                    name={'searchClientNumber'}
                    style={{width: '40px'}}
                    register={register}
                    disabled={!!props.addNewProject ? true : false}
                    defaultVal={!!props?.clientNumber ? props?.clientNumber?.toString() : ''}
                />
                </div>
                {!!props.addNewProject && <RHFInputBox
                    labelOnLeft={true}
                    label="Project Number"
                    defaultVal={'250'}
                    name={'searchProjectNumber'}
                    style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '50%'}}
                    bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '55%'}}
                    register={register}
                />}
                <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Name"
                    name={'searchProjectName'}
                    style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '50%'}}
                    bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '55%'}}
                    register={register}
                />
                {!!props.addNewProject && <RHFInputBox
                    labelOnLeft={true}
                    label="Mileage Rate $"
                    name={'mileageRate'}
                    defaultVal={props?.mileageRate?.toString()}
                    style={{width: '40px'}}
                    register={register}
                />}
                {!!props.addNewProject && <RHFSelect 
                    label="Billing by Method"
                    name={`billingbyMethod`}
                    options={[
                        { value: '0', label: 'Hour/Account' },
                        { value: '0', label: 'Hour/Breakdown' },
                        { value: '0', label: 'Hour/Landman' },
                        { value: '0', label: 'Hour/Project' },
                        { value: '0', label: 'Hour/Section' },
                        { value: '0', label: 'Hour/Tract' },
                        { value: '0', label: 'Hour/Unit' }
                    ]}
                    register={register} 
                    errors={errors}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '25%' }}
                />}
                {!!props.addNewProject && <RHFSelect 
                    label="Originating Office"
                    name={`originatingfOffice`}
                    options={[
                        { value: '0', label: 'OK' },
                        { value: '0', label: 'CO' },
                        { value: '0', label: 'IH' },
                        { value: '0', label: 'MD' },
                        { value: '0', label: 'PA' },
                        { value: '0', label: 'RN' },
                        { value: '0', label: 'TX' }
                    ]}
                    register={register} 
                    errors={errors}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '25%' }}
                />}
                {!!props.addNewProject && <RHFCheckbox
                    name="turnkeyBilling"
                    label="Turnkey Billing?"
                    register={register}
                    errors={errors}
                />}
                {!!props.addNewProject && <RHFInputBox
                    label={'Manager'}
                    labelOnLeft={true}
                    name={'managerNumber'}
                    style={{ width: '30%' }}
                    bodyStyle={{width: '100%' }}
                    register={register}
                />}
                {!!props.addNewProject && <RHFInputBox
                    label={''}
                    labelOnLeft={true}
                    name={'managerName'}
                    register={register}
                />}
                {!!props.addNewProject && <RHFInputBox
                    label={'PC / APM '}
                    labelOnLeft={true}
                    name={'pcapmNumber'}
                    register={register}
                />}
                {!!props.addNewProject && <RHFInputBox
                    label={''}
                    labelOnLeft={true}
                    name={'pcapName'}
                    register={register}
                />}
                {!!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="City"
                    name={'searchProjectCity'}
                    style={{width: '200px'}}
                    register={register}
                />}               
                <div style={{display: 'flex'}}>
                    {!!props.addNewProject && <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="State"
                        name={'searchProjectState'}
                        style={{width: '20px'}}
                        register={register}
                    />}
                    {!!props.addNewProject && <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="County"
                        name={'searchProjectCounty'}
                        style={{width: '20px'}}
                        register={register}
                    />}
                    {!!props.addNewProject && <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="Zip"
                        name={'searchClientZip'}
                        style={{width: '50px'}}
                        register={register}
                    />}
                </div>
                {!!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Area Code"
                    name={'searchAreaCode'}
                    style={{width: '30px'}}
                    register={register}
                />}
                {!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Prospect Name"
                    name={'SearchProspect'}
                    style={{width: '200px'}}
                    register={register}
                />}
            </div>
            <div style={{marginTop: '3rem', overflowY: 'hidden', maxHeight: '200px'}}>
                    {props.addNewProject && <div style={{fontWeight: 'bold', fontFamily: 'Arial', width: '100%', textAlign: 'center'}}>
                        Existing Projects
                    </div>}
                    <table>
                        <thead style={{ textAlign: 'left' }}>
                            <tr>
                                <th>Client #</th>
                                <th>#</th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Manager #</th>
                            </tr>
                        </thead>
                        <tbody onScroll={(event) => getScrollStatus(event.currentTarget)}>
                        {projects && projects?.map((project: ProspectDetails, index: number) => {
                            console.log(projects.length);
                            return (
                                    // tslint:disable-next-line
                                    <tr key={project.number + index} onDoubleClick={() => selectProjectAndClose(project)} onClick={() => setSelectedProject(index) } className="table-item-hover" style={{ textAlign: 'left' }}>
                                        <td style={{ width: '7%' }}>
                                            <RHFInputBox
                                                name={`clientNumber`}
                                                type="text"
                                                defaultVal={props?.clientNumber?.toString()}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '7%' }}>
                                            <RHFInputBox
                                                name={`newProjectNumber`}
                                                type="text"
                                                defaultVal={project?.number?.toString()}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`newProjectName`}
                                                type="text"
                                                defaultVal={project?.name}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`newProjectType`}
                                                type="text"
                                                defaultVal={project?.Type}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`newProjectManager`}
                                                type="text"
                                                defaultVal={project?.manager?.toString()}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </form>
            <div className="form-row" style={{marginTop: '2rem', marginLeft: '33%', textAlign: 'center'}}>
                {!!props.addNewProject && <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Save New Project'}
                    onClick={() => onCreateProject()}
                />}
                {!props.addNewProject && <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'OK'}
                    onClick={() => OKClicked()}
                />}
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => onCancel()}
                />
            </div>
        </Dialog>
        </>
    );
};

export default FindProjectDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/FindProjectDialog.tsx